import React, { useState, useLayoutEffect } from 'react';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ja';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';

import MoveTop from '../../CommonParts';
import changeTime from '../../api/reserveChange/changeTime';
import { useDidMount } from '../../customHooks';
import { saveTimearea, clearTime } from '../../redux_toolkit/reserveChangeSlice';
import TopicPath from '../../components/TopicPath';
import configMastaApi from '../../api/masta/configMasta';
import { ReserveHeader } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { CANCEL_CHANGE_ANNOUNCE } from '../../constants';
import ChangeSelectionSummary from '../../components/ChangeSelectionSummary';

dayjs.extend(utc);
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.locale('ja');

const ChangeTime = function () {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const handleError = useErrorHandler();

  const lgCd = useSelector((state) => state.reserveChange.lgCd);
  const chUrl = useSelector((state) => state.reserveChange.url);
  const businessChangeMode = useSelector((state) => state.reserveChange.businessChangeMode);
  const businessChanged = useSelector((state) => state.reserveChange.businessChanged);
  const rsvConfig = useSelector((state) => state.rsvConfig);
  const { topicPath = [] } = rsvConfig;

  const newDate = location.state.date ?? '';
  const newDayOfWeek = location.state.dayOfWeek ?? '';
  const sameDay = location.state.sameDay ?? false;
  // ChangeForm / ChangeConfirmation から「前に戻る」で来た場合に sameTime が渡される
  // ChangeDate から直接来た場合は undefined → false になる
  const sameTime = location.state?.sameTime ?? false;
  const bookingTimearea = useSelector((state) => state.reserveChange.timearea);

  const rsvSetId = useSelector((state) => state.reserveChange.rsvSetId);

  const [dayInfo, setDayInfo] = useState({});
  const [rsvInterval, setRsvInterval] = useState('');
  const [taWidth, setTaWidth] = useState('23%');
  const [taColumns, setTaColumns] = useState(4);
  const [pageName, setPageName] = useState('');
  const [backUrl, setBackUrl] = useState('');
  const [remainDisplay, setRemainDisplay] = useState(false);

  // パンくずリスト設定から、ページ名・前の画面のURLをstateに設定(予約変更)
  const getBackUrlAndPageName = (topicPathConf) => {
    const locationPath = window.location.pathname;
    const tmpPath1 = locationPath.replace('/reserve/', '');
    const tmpPath2 = tmpPath1.replace('/test', '');
    const currentPath = tmpPath2.replace('/demo', '');

    let backUrl = '';
    let scnName = '';
    for (let i = 0; i < topicPathConf.length; i += 1) {
      const { url = '', scnName: tmpScnName = '' } = topicPathConf[i];
      if (currentPath === url) {
        // 「前に戻る」先のURL取得
        const { url: tmpBackUrl = '' } = topicPathConf[i - 1];
        if (tmpBackUrl === 'change/:url') {
          backUrl = `change/${chUrl}`;
        } else {
          backUrl = tmpBackUrl;
        }

        // pageNameを取得
        scnName = tmpScnName;

        break;
      }
    }

    setPageName(scnName);
    setBackUrl(backUrl);
  };

  const init = async () => {
    // ページ名・戻り先URLの取得
    getBackUrlAndPageName(topicPath);

    // 選択した日の予約時間情報を取得
    const startDay = newDate;
    const {
      dayInfoList = {},
      rsvInterval = '',
      remainDisplay = false,
    } = await changeTime
      .getDayInfo(lgCd, startDay, sameDay, bookingTimearea, rsvSetId)
      .catch((err) => {
        window.alert('予約時間の取得に失敗しました。');
        handleError(err);
        return '';
      });

    // タイムエリアの幅設定取得
    const widthConf = await configMastaApi
      .findByConfCd(`timearea_width_${rsvInterval}`)
      .catch((err) => {
        window.alert('時間設定の取得に失敗しました。');
        handleError(err);
        return '';
      });
    const { value: widthVal = '23%' } = widthConf;

    // タイムエリアの列数設定取得
    const columnsConf = await configMastaApi
      .findByConfCd(`timearea_culumns_${rsvInterval}`)
      .catch((err) => {
        window.alert('時間設定の取得に失敗しました。');
        handleError(err);
        return '';
      });
    const { value: columnsVal = 4 } = columnsConf;
    const columnsNum = parseInt(columnsVal, 10);

    setDayInfo(dayInfoList);
    setRsvInterval(rsvInterval);
    setTaWidth(widthVal);
    setTaColumns(columnsNum);
    setRemainDisplay(remainDisplay);
  };

  // sameDay=false のとき、ChangeForm/ChangeConfirmation から戻ってきた際に
  // Redux の古い rsvDateWithTime をクリアして利用時間を非表示にする
  // useLayoutEffect = ブラウザ描画前に同期実行 → チラつきなし
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!sameDay) {
      dispatch(clearTime());
    }
  }, []);

  useDidMount(() => {
    init();
  });

  const moveToReserve = (timearea, sameTime = false) => {
    // ここでdate型文字列を作る
    const rsvDateWithTime = `${newDate} ${timearea.slice(0, timearea.length - 2)}:${timearea.slice(
      -2
    )}`;
    // 選択した時間を Redux に保存 → 選択内容パネルをリアルタイム更新
    dispatch(
      saveTimearea({
        timearea,
        rsvDate: newDate,
        rsvDayOfWeek: newDayOfWeek,
        rsvFlameId: 0,
        rsvDateWithTime,
      })
    );
    // ChangeForm を経由するか判定する
    // モード①(=1)で用件が変更された場合、またはモード②(=2)の場合は ChangeForm へ
    // モード③(=3)および モード①で用件変更なしの場合は ChangeConfirmation へ
    const needsChangeForm =
      (businessChangeMode === 1 && businessChanged === true) || businessChangeMode === 2;

    const destination = needsChangeForm ? '/change/userinfo' : '/change/confirmation';
    navigate(destination, {
      state: {
        newTimearea: timearea,
        date: newDate,
        newDayOfWeek,
        rsvFlameId: 0,
        newRsvDateWithTime: rsvDateWithTime,
        sameDay,
        sameTime,
      },
    });
  };

  const createTimeSelector = (dayInfo) => {
    const keys1 = Object.keys(dayInfo);
    const timeSelector = [];

    for (let h = 0; h < keys1.length; h += 1) {
      const currentKey1 = keys1[h];
      const currentInfo = dayInfo[currentKey1];
      const keys2 = Object.keys(currentInfo);
      if (keys2.length !== 0) {
        const firsttime = currentInfo[keys2[0]]?.display;
        const firstMinutes = firsttime.substring(firsttime.length - 2);
        if (firstMinutes !== '00') {
          const firstMinutesNum = parseInt(firstMinutes, 10);
          const intervalNum = parseInt(rsvInterval, 10);
          const blankNum = firstMinutesNum / intervalNum;
          for (let blankidx = 0; blankidx < parseInt(blankNum, 10); blankidx += 1) {
            timeSelector.push(<li className="notset" style={{ width: taWidth }} />);
          }
        }
      }

      for (let i = 0; i < keys2.length; i += 1) {
        const currentKey2 = keys2[i];
        const { display = '', enable = false, rsvStat = '', className } = currentInfo[currentKey2];

        if (sameDay && currentKey2 === bookingTimearea) {
          timeSelector.push(
            <li className={className} style={{ width: taWidth, background: '#fffaba' }}>
              <a
                role="button"
                // href="javascript:void(0);"
                onClick={() => moveToReserve(currentKey2, true)}
                tabIndex={0}
              >
                {display}
                <span>{rsvStat}</span>
              </a>
            </li>
          );
        } else if (enable) {
          timeSelector.push(
            <li className={className} style={{ width: taWidth }}>
              <a
                role="button"
                // href="javascript:void(0);"
                onClick={() => moveToReserve(currentKey2)}
                tabIndex={0}
              >
                {display}
                <span>{rsvStat}</span>
              </a>
            </li>
          );
        } else {
          timeSelector.push(
            <li className={className} style={{ width: taWidth }}>
              {display}
              <span>{rsvStat}</span>
            </li>
          );
        }
      }
    }

    // if (innerWidth >= 481) {
    // while (timeSelector.length % taColumns !== 0) {
    // ゼロ除算はNaNになるため taColumnsが0の場合は実施しない
    while (taColumns !== 0 && timeSelector.length % taColumns !== 0) {
      timeSelector.push(<li className="notset" style={{ width: taWidth }} />);
    }
    // }

    return timeSelector;
  };

  const onClickBack = () => {
    // sameTime=false かつ「同じ日 & 用件変更なし」ではない場合だけ clearTime する
    // 同じ日 & 用件変更なし のときは Redux に変更前の時間を残し、ChangeDate のプレビューに表示する
    if (!sameTime && !(sameDay && !businessChanged)) {
      dispatch(clearTime());
    }
    // sameDay を渡すことで ChangeDate 側が「同じ日からの戻り」かを判別できるようにする
    navigate(`/${backUrl}`, { state: { cameFromBack: true, sameDay } });
  };

  // 空き状況の説明部分を作成
  const createRemainExplain = () => {
    if (remainDisplay) {
      return (
        <p id="reserve_text">
          <span className="possible">◯</span> 予約可能　<span className="few">△</span> 残りわずか
          <span className="reserved">×</span> 予約済み
          <span className="notset">&nbsp;&nbsp;&nbsp;&nbsp;</span> 予約不可
        </p>
      );
    }
    return (
      <p id="reserve_text">
        <span className="possible">◯</span> 予約可能　<span className="reserved">×</span> 予約済み
        <span className="notset">&nbsp;&nbsp;&nbsp;&nbsp;</span> 予約不可
      </p>
    );
  };

  return (
    <>
      <ReserveHeader servCd="reserve" lgCd={lgCd} />
      <div id="container">
        <TopicPath />
        <main>
          <article>
            <ChangeSelectionSummary showNote />
            <div id="contents">
              <MoveTop lgCd={lgCd} />
              <div className="inner630">
                <h2>{pageName}</h2>
                <p className="point">{dayjs(newDate).format('YYYY[年]M[月]D[日](ddd)')}</p>
                <p className="txtCenter">
                  利用時間を選択してください。
                  <br />
                  <span className="fs_14px">
                    ※予約の残り枠数は変動しますので、希望の日時に予約いただけない場合がございます。
                  </span>
                </p>
                <div id="timeBox">
                  <ul id="selectTime" className="row jcSB">
                    {createTimeSelector(dayInfo)}
                  </ul>
                  {createRemainExplain()}
                </div>
                <p id="navi" className="row jcC">
                  <a
                    role="button"
                    // href="javascript:void(0);"
                    onClick={() => onClickBack()}
                    className="btn btBack"
                    tabIndex={0}
                  >
                    前に戻る
                  </a>
                </p>
              </div>
              <div className="cancelAnnounce">{CANCEL_CHANGE_ANNOUNCE}</div>
            </div>
          </article>
        </main>
      </div>
      <Footer />
    </>
  );
};

export default ChangeTime;
